import React from 'react';
import {ApolloProvider} from '@apollo/client';
import {CookiesProvider} from 'react-cookie';
import {Helmet} from 'react-helmet';
import {I18nextProvider} from 'react-i18next';
import {Route, Switch} from 'react-router-dom';
import {EzThemeProvider} from '@ezcater/recipe';
import {Tooltip} from '@ezcater/tapas';
import {FlashMessagesProvider} from '@ezcater/flash-messages';
import {HEALTH_CHECK_PATH} from './paths';
import ErrorBoundary from './components/ErrorBoundary';
import {EnvironmentProvider} from './components/Environment';
import FlashMessage from './components/FlashMessage';
import i18n from './components/i18n';
import WithEzManagePermissions from './components/WithEzManagePermissions';
import Routes from './Routes';
import {theme} from './styles';
import EzTrackingProvider from './components/tracking/EzTrackingProvider';
import {DirectConnectApolloClientProvider} from './components/DirectConnectApolloClientContext';
import {EzRailsUploadApolloClientProvider} from './components/EzRailsUploadApolloClientContext';
import {TrackingHookProvider} from './hooks/useTracking';
import DeliveryManagementRoutes from './components/DeliveryManagementRoutes';
import AssignmentsProvider from './components/AssignmentsProvider/AssignmentsProvider';
import LazyMotion from './components/LazyMotion';

const AppLevelProviders = ({
  children,
  environmentVars,
  directConnectApolloClient,
  ezRailsUploadClient,
  apolloClient,
  cookies,
  assignmentsConfiguration,
  requestUrl,
}) => {
  const trackingId = cookies?.get('tid') || '';
  return (
    <EnvironmentProvider environmentVars={environmentVars}>
      <DirectConnectApolloClientProvider apolloClient={directConnectApolloClient}>
        <EzRailsUploadApolloClientProvider apolloClient={ezRailsUploadClient}>
          <ApolloProvider client={apolloClient}>
            <CookiesProvider cookies={cookies}>
              <ErrorBoundary>
                <LazyMotion>
                  <Tooltip.Provider>
                    <EzThemeProvider theme={theme}>
                      <AssignmentsProvider
                        flagsConfiguration={assignmentsConfiguration}
                        requestUrl={requestUrl}
                        trackingId={trackingId}
                      >
                        <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
                      </AssignmentsProvider>
                    </EzThemeProvider>
                  </Tooltip.Provider>
                </LazyMotion>
              </ErrorBoundary>
            </CookiesProvider>
          </ApolloProvider>
        </EzRailsUploadApolloClientProvider>
      </DirectConnectApolloClientProvider>
    </EnvironmentProvider>
  );
};

const RouteLevelProviders = ({children}) => (
  <EzTrackingProvider>
    <FlashMessagesProvider FlashMessageTemplate={FlashMessage}>
      <TrackingHookProvider>{children}</TrackingHookProvider>
    </FlashMessagesProvider>
  </EzTrackingProvider>
);

const App = ({
  apolloClient,
  assignmentsConfiguration,
  directConnectApolloClient,
  ezRailsUploadClient,
  context,
  cookies,
  environmentVars,
  location,
  loggedOutListener,
  navFFstate,
  requestUrl: serverRequestUrl,
  router: Router,
}) => {
  const requestUrl = typeof window === 'undefined' ? serverRequestUrl : window.location.href;
  const isDeliveryManagementApp =
    !!requestUrl &&
    !!environmentVars.deliveryManagementUiHost &&
    requestUrl.includes(new URL(environmentVars.deliveryManagementUiHost).hostname);

  return (
    <AppLevelProviders
      assignmentsConfiguration={assignmentsConfiguration}
      environmentVars={environmentVars}
      directConnectApolloClient={directConnectApolloClient}
      ezRailsUploadClient={ezRailsUploadClient}
      apolloClient={apolloClient}
      cookies={cookies}
      navFFstate={navFFstate}
      requestUrl={requestUrl}
    >
      <React.Fragment>
        <Helmet>
          <title>{i18n.t('pageTitles.default')}</title>
        </Helmet>
        <Router location={location} context={context}>
          <RouteLevelProviders>
            {isDeliveryManagementApp ? (
              <DeliveryManagementRoutes />
            ) : (
              <>
                <Routes loggedOutListener={loggedOutListener} requestUrl={requestUrl} />
                <Switch>
                  {/*
                    Exclude the health check path from rendering components that make
                    the assumption there is a logged in user when making a query
                    in order to stop any error noise
                  */}
                  <Route exact path={HEALTH_CHECK_PATH} render={() => null} />
                  <Route
                    path="*"
                    render={() => (
                      <React.Fragment>
                        {/*
                          We prefetch permissions as a performance optimizations.
                          By fetching here any usage in page level components will already have data present.
                        */}
                        <WithEzManagePermissions>{() => null}</WithEzManagePermissions>
                      </React.Fragment>
                    )}
                  />
                </Switch>
              </>
            )}
          </RouteLevelProviders>
        </Router>
      </React.Fragment>
    </AppLevelProviders>
  );
};

export default App;
